import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import CtaBand from './CtaBand';
import usePageMeta, {breadcrumbJsonLd} from '../seo/usePageMeta';
import company from '../config/company';
import './Pages.css';
import './Faq.css';

// Alle Antworten stützen sich auf Angaben der Website (Leistungen, Pflegekosten,
// Kontakt) und allgemeine gesetzliche Regelungen (SGB V und SGB XI).
export const faqItems = [
  {
    id: 'geeignet',
    question: 'Für wen ist ein ambulanter Pflegedienst geeignet?',
    answer:
      'Für alle, die zu Hause Unterstützung brauchen: nach einem Krankenhausaufenthalt, bei chronischer Erkrankung, im Alter oder in der letzten Lebensphase. Auch pflegende Angehörige entlasten wir, zum Beispiel durch Verhinderungspflege oder Beratung.'
  },
  {
    id: 'leistungen',
    question: 'Welche Leistungen bietet Cura Care an?',
    answer:
      'Behandlungspflege nach ärztlicher Verordnung, Grundpflege, Wundmanagement, Palliativpflege, Verhinderungspflege, Pflegeberatung und eine 24h-Rufbereitschaft. Über unsere Pflegeakademie qualifizieren wir außerdem Alltagsbegleiter nach § 45a SGB XI.',
    link: {to: '/leistungen', label: 'Alle Leistungen ansehen'}
  },
  {
    id: 'kosten',
    question: 'Wer übernimmt die Kosten für die Pflege?',
    answer:
      'Je nach Leistung die Pflegekasse, die Krankenkasse, die Beihilfe oder Sie selbst. Wir sind Vertragspartner aller gesetzlichen Pflege- und Krankenkassen und rechnen direkt mit den Kostenträgern ab. Mehrere Kostenträger können kombiniert werden.',
    link: {to: '/pflegekosten', label: 'Pflegekosten und Pflegegrade 2026'}
  },
  {
    id: 'pflegegrad',
    question: 'Brauche ich einen Pflegegrad?',
    answer:
      'Für Grundpflege über die Pflegekasse ja: Ab Pflegegrad 2 stehen Ihnen Pflegesachleistungen zu. Behandlungspflege wie Medikamentengabe, Injektionen oder Verbandswechsel wird dagegen ärztlich verordnet und über die Krankenkasse abgerechnet. Dafür ist kein Pflegegrad nötig.'
  },
  {
    id: 'antrag',
    question: 'Wie beantrage ich einen Pflegegrad?',
    answer:
      'Der Antrag wird formlos oder per Formular bei der Pflegekasse gestellt. Entscheidend für den Leistungsbeginn ist das Eingangsdatum. Anschließend begutachtet der Medizinische Dienst die Situation zu Hause. Wir unterstützen Sie beim Antrag, bei der Vorbereitung auf die Begutachtung und bei einem Widerspruch.'
  },
  {
    id: 'start',
    question: 'Wie läuft der Start mit Cura Care ab?',
    answer:
      'Sie rufen an oder schreiben uns. Wir besprechen persönlich, welche Unterstützung gebraucht wird, und planen dann gemeinsam die Versorgung. Verordnungen, Anträge und die Abrechnung mit den Kassen übernehmen wir für Sie.',
    link: {to: '/kontakt', label: 'Kontakt aufnehmen'}
  },
  {
    id: 'region',
    question: 'Wo ist Cura Care tätig?',
    answer: `Unser Büro liegt in der ${company.street} in ${company.city}. Wir versorgen Kundinnen und Kunden in Eitorf und Umgebung und klären gern, ob eine Versorgung an Ihrem Wohnort möglich ist.`
  },
  {
    id: 'erreichbar',
    question: 'Wann sind Sie erreichbar?',
    answer: `Das Büro ist montags bis freitags von ${company.openingHours.opens} bis ${company.openingHours.closes} Uhr besetzt. Für dringende Anliegen erreichen Sie unsere Rufbereitschaft rund um die Uhr unter ${company.phone}.`
  },
  {
    id: 'kombination',
    question: 'Kann ich Pflegegeld und Pflegedienst kombinieren?',
    answer:
      'Ja. Wenn Angehörige einen Teil der Pflege übernehmen und wir ergänzend unterstützen, wird die Kombinationsleistung genutzt: Das Pflegegeld wird anteilig weitergezahlt, der übrige Anteil wird als Pflegesachleistung über uns abgerechnet.'
  },
  {
    id: 'verhinderungspflege',
    question: 'Was ist Verhinderungspflege und wie viel steht mir zu?',
    answer:
      'Verhinderungspflege springt ein, wenn pflegende Angehörige durch Urlaub, Krankheit oder eine Auszeit verhindert sind. Ab Pflegegrad 2 stehen dafür 2026 bis zu 3.539 Euro im Jahr zur Verfügung, gemeinsam mit der Kurzzeitpflege. Wir übernehmen die Pflege geplant oder kurzfristig.',
    link: {to: '/leistungen/verhinderungspflege', label: 'Mehr zur Verhinderungspflege'}
  },
  {
    id: 'beratungsbesuch',
    question: 'Bieten Sie Beratungsbesuche nach § 37 Abs. 3 SGB XI an?',
    answer:
      'Ja. Wer Pflegegeld bezieht, muss regelmäßig einen Beratungsbesuch nachweisen: bei Pflegegrad 2 und 3 halbjährlich, bei Pflegegrad 4 und 5 vierteljährlich. Die Kosten trägt die Pflegekasse. Wir führen die Besuche durch und beraten dabei zu Entlastungsmöglichkeiten.'
  },
  {
    id: 'akademie',
    question: 'Was ist die Cura Care Pflegeakademie?',
    answer:
      'Unsere Akademie qualifiziert Alltagsbegleiter nach § 45a SGB XI: ein Basiskurs mit 40 Stunden für 445 Euro und ein jährlicher Refresher mit 16 Stunden für 245 Euro, jeweils mit fachlicher Begleitung und Zertifikat.',
    link: {to: '/leistungen/pflegeakademie', label: 'Zur Pflegeakademie'}
  }
];

const Faq = () => {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {'@type': 'Answer', text: item.answer}
        }))
      },
      breadcrumbJsonLd([
        {name: 'Startseite', path: '/'},
        {name: 'Häufige Fragen', path: '/faq'}
      ])
    ],
    []
  );

  usePageMeta({
    title: 'Häufige Fragen zur ambulanten Pflege | Cura Care Eitorf',
    description:
      'Antworten auf die häufigsten Fragen zu Pflegegrad, Kosten, Verordnung, Verhinderungspflege und dem Start der Pflege mit Cura Care in Eitorf.',
    path: '/faq',
    jsonLd
  });

  return (
    <div className="page-view">
      <Header />
      <main className="page faq-page" id="faq">
        <div className="page__inner">
          <nav className="breadcrumb" aria-label="Brotkrumen">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <span>Häufige Fragen</span>
          </nav>

          <div className="page__header">
            <p className="kicker">Fragen und Antworten</p>
            <h1>Häufige Fragen zur ambulanten Pflege</h1>
            <p>
              Verständliche Antworten zu Pflegegrad, Kosten und dem Ablauf bei Cura Care. Ihre Frage
              ist nicht dabei? Rufen Sie uns an oder schreiben Sie uns.
            </p>
          </div>

          <div className="faq-list">
            {faqItems.map((item, index) => (
              <details className="faq-item" key={item.id} open={index === 0}>
                <summary>
                  <span>{item.question}</span>
                  <span className="faq-item__chevron" aria-hidden="true" />
                </summary>
                <div className="faq-item__body">
                  <p>{item.answer}</p>
                  {item.link ? <Link to={item.link.to}>{item.link.label}</Link> : null}
                </div>
              </details>
            ))}
          </div>
        </div>
      </main>
      <CtaBand />
      <Footer />
    </div>
  );
};

export default Faq;
